/**
 * Generador de códigos QR, mínimo y propio.
 *
 * Existe por una razón muy concreta: el QR de la factura de GTI codifica
 * `https://www.facturaelectronica.co.cr/QR/` + la `<Clave>` del comprobante, y
 * la clave está en el XML original. Es decir, **el QR es reproducible carácter
 * por carácter** desde la misma fuente que el resto de la vista, sin inventar
 * nada. Lo que faltaba era dibujarlo.
 *
 * Se escribió a mano en vez de traer una librería porque el caso de uso es de
 * una sola forma —una URL corta, siempre la misma estructura— y una dependencia
 * nueva en el bundle del cliente cuesta más que estas líneas.
 *
 * Alcance deliberadamente recortado a lo que la factura necesita:
 *   · modo byte (la URL es ASCII, pero se codifica UTF-8 igual);
 *   · corrección de errores **M**, versiones 1 a 9.
 * Esos dos parámetros no son un gusto: son los que usa el QR de GTI (versión 6,
 * nivel M), verificados decodificando el QR del PDF original. Reproducirlos hace
 * que la matriz salga idéntica módulo a módulo, no solo equivalente al escanear.
 *
 * Si el texto no cabe en la versión 9, devuelve `null` — nunca un QR truncado
 * que apunte a otro lado. Un QR equivocado es peor que ningún QR.
 *
 * Referencia: ISO/IEC 18004.
 */

/** Nivel M: el que usa GTI. Sus dos bits en la información de formato. */
const ECC_FORMAT_BITS = 0b00;

/** Codewords totales (datos + corrección) por versión, 1 a 9. */
const TOTAL_CODEWORDS = [26, 44, 70, 100, 134, 172, 196, 242, 292];
/** Codewords de corrección por bloque, nivel M. */
const EC_PER_BLOCK = [10, 16, 26, 18, 24, 16, 18, 22, 22];
/** Cantidad de bloques, nivel M. */
const NUM_BLOCKS = [1, 1, 1, 2, 2, 4, 4, 4, 5];
/** Centros de los patrones de alineación por versión. */
const ALIGN_POS: number[][] = [
  [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46],
];

/* ── Aritmética en GF(256) ────────────────────────────────────────────────── */

/** Multiplicación en el campo del QR (polinomio primitivo 0x11D). */
function gfMul(a: number, b: number): number {
  let r = 0;
  for (let i = 7; i >= 0; i--) {
    r = (r << 1) ^ ((r >> 7) * 0x11d);
    r ^= ((b >> i) & 1) * a;
  }
  return r & 0xff;
}

/** El polinomio generador de Reed-Solomon para `degree` codewords. */
function rsGenerator(degree: number): number[] {
  const poly = new Array<number>(degree).fill(0);
  poly[degree - 1] = 1;
  let root = 1;
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      poly[j] = gfMul(poly[j]!, root);
      if (j + 1 < degree) poly[j] = poly[j]! ^ poly[j + 1]!;
    }
    root = gfMul(root, 2);
  }
  return poly;
}

/** El resto de dividir los datos por el generador: los codewords de corrección. */
function rsRemainder(data: number[], generator: number[]): number[] {
  const result = new Array<number>(generator.length).fill(0);
  for (const byte of data) {
    const factor = byte ^ result.shift()!;
    result.push(0);
    for (let i = 0; i < generator.length; i++) result[i] = result[i]! ^ gfMul(generator[i]!, factor);
  }
  return result;
}

/* ── Corrección BCH para formato y versión ───────────────────────────────── */

function bch(data: number, generator: number, bits: number): number {
  let rem = data;
  for (let i = 0; i < bits; i++) rem = (rem << 1) ^ ((rem >>> (bits - 1)) * generator);
  return ((data << bits) | rem) >>> 0;
}

/* ── Máscaras ─────────────────────────────────────────────────────────────── */

const MASKS: ((x: number, y: number) => boolean)[] = [
  (x, y) => (x + y) % 2 === 0,
  (_x, y) => y % 2 === 0,
  (x) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

/* ── Construcción ─────────────────────────────────────────────────────────── */

interface Canvas {
  size: number;
  modules: boolean[][];
  /** Qué celdas son patrón fijo: la máscara no las toca y los datos las saltan. */
  reserved: boolean[][];
}

function blank(size: number): Canvas {
  return {
    size,
    modules: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
    reserved: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
  };
}

function place(c: Canvas, x: number, y: number, dark: boolean): void {
  if (x < 0 || y < 0 || x >= c.size || y >= c.size) return;
  c.modules[y]![x] = dark;
  c.reserved[y]![x] = true;
}

function drawFinder(c: Canvas, cx: number, cy: number): void {
  // El separador claro alrededor entra en el mismo barrido: por eso el rango
  // llega a ±4 aunque el patrón en sí mida 7×7.
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      const d = Math.max(Math.abs(dx), Math.abs(dy));
      place(c, cx + dx, cy + dy, d !== 2 && d <= 3);
    }
  }
}

function drawFunctionPatterns(c: Canvas, version: number): void {
  const n = c.size;

  for (let i = 0; i < n; i++) {
    place(c, 6, i, i % 2 === 0);
    place(c, i, 6, i % 2 === 0);
  }

  drawFinder(c, 3, 3);
  drawFinder(c, n - 4, 3);
  drawFinder(c, 3, n - 4);

  const pos = ALIGN_POS[version - 1]!;
  for (const cy of pos) {
    for (const cx of pos) {
      // Los tres cruces que caen sobre un buscador no llevan alineación.
      const onFinder = (cx === pos[0] && cy === pos[0])
        || (cx === pos[0] && cy === pos[pos.length - 1])
        || (cx === pos[pos.length - 1] && cy === pos[0]);
      if (onFinder) continue;
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          place(c, cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
        }
      }
    }
  }

  // Reserva del formato (se rellena cuando ya se eligió la máscara) y el
  // módulo oscuro obligatorio. El índice 6 se salta: ahí cruza el patrón de
  // temporización, que la información de formato bordea en vez de pisar.
  for (let i = 0; i <= 8; i++) {
    if (i === 6) continue;
    place(c, 8, i, false);
    place(c, i, 8, false);
  }
  for (let i = 0; i < 8; i++) { place(c, n - 1 - i, 8, false); place(c, 8, n - 1 - i, false); }
  place(c, 8, n - 8, true);

  if (version >= 7) {
    const bits = bch(version, 0x1f25, 12);
    for (let i = 0; i < 18; i++) {
      const bit = ((bits >>> i) & 1) === 1;
      place(c, i % 3 + n - 11, Math.floor(i / 3), bit);
      place(c, Math.floor(i / 3), i % 3 + n - 11, bit);
    }
  }
}

function drawFormat(c: Canvas, mask: number): void {
  const bits = (bch((ECC_FORMAT_BITS << 3) | mask, 0x537, 10) ^ 0x5412) >>> 0;
  const bit = (i: number) => ((bits >>> i) & 1) === 1;
  const n = c.size;

  for (let i = 0; i <= 5; i++) place(c, 8, i, bit(i));
  place(c, 8, 7, bit(6));
  place(c, 8, 8, bit(7));
  place(c, 7, 8, bit(8));
  for (let i = 9; i < 15; i++) place(c, 14 - i, 8, bit(i));

  for (let i = 0; i < 8; i++) place(c, n - 1 - i, 8, bit(i));
  for (let i = 8; i < 15; i++) place(c, 8, n - 15 + i, bit(i));
  place(c, 8, n - 8, true);
}

/** Codewords finales: datos con relleno + corrección, ya intercalados. */
function buildCodewords(bytes: number[], version: number): number[] {
  const total = TOTAL_CODEWORDS[version - 1]!;
  const ecLen = EC_PER_BLOCK[version - 1]!;
  const blocks = NUM_BLOCKS[version - 1]!;
  const dataLen = total - ecLen * blocks;

  const bits: number[] = [];
  const push = (value: number, width: number) => {
    for (let i = width - 1; i >= 0; i--) bits.push((value >>> i) & 1);
  };
  push(0b0100, 4);          // modo byte
  push(bytes.length, 8);    // versiones 1-9: contador de 8 bits
  for (const b of bytes) push(b, 8);

  push(0, Math.min(4, dataLen * 8 - bits.length));   // terminador
  push(0, (8 - (bits.length % 8)) % 8);              // hasta cerrar el byte

  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let k = 0; k < 8; k++) byte = (byte << 1) | bits[i + k]!;
    data.push(byte);
  }
  // Relleno alterno 0xEC/0x11, tal como manda la norma.
  for (let i = 0; data.length < dataLen; i++) data.push(i % 2 === 0 ? 0xec : 0x11);

  const short = Math.floor(dataLen / blocks);
  const longCount = dataLen % blocks;
  const generator = rsGenerator(ecLen);

  const dataBlocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;
  for (let b = 0; b < blocks; b++) {
    const len = short + (b >= blocks - longCount ? 1 : 0);
    const chunk = data.slice(offset, offset + len);
    offset += len;
    dataBlocks.push(chunk);
    ecBlocks.push(rsRemainder(chunk, generator));
  }

  const out: number[] = [];
  for (let i = 0; i <= short; i++) {
    for (const b of dataBlocks) if (i < b.length) out.push(b[i]!);
  }
  for (let i = 0; i < ecLen; i++) for (const b of ecBlocks) out.push(b[i]!);
  return out;
}

function drawData(c: Canvas, codewords: number[]): void {
  let i = 0;
  const total = codewords.length * 8;
  for (let right = c.size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // la columna de temporización no cuenta
    for (let vert = 0; vert < c.size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? c.size - 1 - vert : vert;
        if (c.reserved[y]![x] || i >= total) continue;
        c.modules[y]![x] = ((codewords[i >>> 3]! >>> (7 - (i & 7))) & 1) === 1;
        i++;
      }
    }
  }
}

/** La penalización de la norma: cuanto más baja, mejor se lee la máscara. */
function penalty(c: Canvas): number {
  const n = c.size;
  const at = (x: number, y: number) => c.modules[y]![x]!;
  let score = 0;

  // Regla 1 — corridas de 5 o más del mismo tono.
  for (let i = 0; i < n; i++) {
    for (const byRow of [true, false]) {
      let run = 1;
      for (let j = 1; j < n; j++) {
        const prev = byRow ? at(j - 1, i) : at(i, j - 1);
        const cur = byRow ? at(j, i) : at(i, j);
        if (cur === prev) { run++; if (run === 5) score += 3; else if (run > 5) score += 1; }
        else run = 1;
      }
    }
  }

  // Regla 2 — bloques de 2×2 del mismo tono.
  for (let y = 0; y < n - 1; y++) {
    for (let x = 0; x < n - 1; x++) {
      const v = at(x, y);
      if (v === at(x + 1, y) && v === at(x, y + 1) && v === at(x + 1, y + 1)) score += 3;
    }
  }

  // Regla 3 — la silueta 1:1:3:1:1 con su margen claro, que confunde al lector
  // con un patrón buscador.
  const A = [true, false, true, true, true, false, true, false, false, false, false];
  const B = [...A].reverse();
  const matches = (get: (k: number) => boolean, start: number, pattern: boolean[]) =>
    pattern.every((p, k) => get(start + k) === p);
  for (let i = 0; i < n; i++) {
    for (let j = 0; j + 11 <= n; j++) {
      if (matches((k) => at(k, i), j, A) || matches((k) => at(k, i), j, B)) score += 40;
      if (matches((k) => at(i, k), j, A) || matches((k) => at(i, k), j, B)) score += 40;
    }
  }

  // Regla 4 — desbalance entre claro y oscuro.
  let dark = 0;
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) if (at(x, y)) dark++;
  score += Math.floor(Math.abs(dark * 20 - n * n * 10) / (n * n)) * 10;

  return score;
}

/**
 * La matriz de módulos de `text`, o `null` si no cabe en las versiones que este
 * generador cubre. `true` es módulo oscuro. No incluye zona de silencio.
 */
export function qrMatrix(text: string): boolean[][] | null {
  const bytes = [...new TextEncoder().encode(text)];

  let version = 0;
  for (let v = 1; v <= 9; v++) {
    const capacity = TOTAL_CODEWORDS[v - 1]! - EC_PER_BLOCK[v - 1]! * NUM_BLOCKS[v - 1]!;
    if (bytes.length + 2 <= capacity) { version = v; break; } // +2: modo y contador
  }
  if (version === 0) return null;

  const codewords = buildCodewords(bytes, version);

  let best: Canvas | null = null;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const c = blank(version * 4 + 17);
    drawFunctionPatterns(c, version);
    drawData(c, codewords);
    for (let y = 0; y < c.size; y++) {
      for (let x = 0; x < c.size; x++) {
        if (!c.reserved[y]![x] && MASKS[mask]!(x, y)) c.modules[y]![x] = !c.modules[y]![x];
      }
    }
    drawFormat(c, mask);
    const score = penalty(c);
    if (score < bestScore) { bestScore = score; best = c; }
  }

  return best!.modules;
}

/**
 * El QR como SVG independiente, listo para un `src` o un `dangerouslySetInner…`.
 * Se dibuja como un solo `<path>` porque un rect por módulo son ~1.700 nodos y
 * el navegador los tiene que imprimir.
 */
export function qrSvg(text: string, quietZone = 4): string | null {
  const m = qrMatrix(text);
  if (!m) return null;
  const n = m.length;
  const side = n + quietZone * 2;

  let d = '';
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      if (m[y]![x]) d += `M${x + quietZone} ${y + quietZone}h1v1h-1z`;
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${side} ${side}" shape-rendering="crispEdges">`
    + `<rect width="${side}" height="${side}" fill="#fff"/>`
    + `<path d="${d}" fill="#000"/></svg>`;
}
